import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ImageModule } from 'primeng/image';

import { HospicashSettingService, HospicashSetting, HospicashSettingDto } from '../../services/hospicash-setting';
import { FileService } from '../../services/file';
import { DocUploadComponent } from '../../shared/doc-upload';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

/**
 * Hospicash Settings — company letterhead/contacts + 3 uploaded images (letterhead, signature,
 * stamp) printed on the partner welcome letter. One active config is used at render time.
 */
@Component({
  selector: 'app-hospicash-setting',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    ImageModule,
    DocUploadComponent,
  ],
  templateUrl: './hospicash-setting.html',
  styleUrls: ['./hospicash-setting.css'],
})
export class HospicashSettingPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(HospicashSettingService);
  private fileSvc = inject(FileService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  rows = signal<HospicashSetting[]>([]);

  /** guid → presigned URL cache (for previews + list thumbnails). */
  imgUrls = signal<Record<string, string>>({});
  letterheadPreview = signal('');
  signaturePreview = signal('');
  stampPreview = signal('');

  visible = computed(() => [...this.rows()].sort((a, b) => Number(b.isActive) - Number(a.isActive) || b.id - a.id));

  form: FormGroup = this.fb.group({
    id: [0],
    companyName: ['', Validators.required],
    companyShortName: [''],
    addressLine: [''],
    customerCare: [''],
    email: [''],
    website: [''],
    hospicashClaimNumber: [''],
    docAssistanceNumber: [''],
    letterheadImage: [''],
    signatureImage: [''],
    stampImage: [''],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.form.get('letterheadImage')!.valueChanges.subscribe((v) => this.setPreview('letterhead', v));
    this.form.get('signatureImage')!.valueChanges.subscribe((v) => this.setPreview('signature', v));
    this.form.get('stampImage')!.valueChanges.subscribe((v) => this.setPreview('stamp', v));
    this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.all().subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load settings.');
          this.rows.set([]);
          return;
        }
        const list = res?.data ?? [];
        this.rows.set(list);
        for (const r of list) this.resolveImg(r.letterheadImage);
      },
      error: () => this.loading.set(false),
    });
  }

  /** Resolve a stored guid → presigned URL (cached). http/data URIs render as-is. */
  private resolveImg(v?: string | null) {
    const s = (v ?? '').trim();
    if (!s || /^(https?:\/\/|data:)/i.test(s) || this.imgUrls()[s]) return;
    this.fileSvc.url(s).subscribe({ next: (u) => { if (u) this.imgUrls.update((m) => ({ ...m, [s]: u })); } });
  }

  imgSrc(v?: string | null): string {
    const s = (v ?? '').trim();
    if (!s) return '';
    if (/^(https?:\/\/|data:)/i.test(s)) return s;
    return this.imgUrls()[s] ?? '';
  }

  private setPreview(which: 'letterhead' | 'signature' | 'stamp', v: string | null | undefined) {
    const sig = which === 'letterhead' ? this.letterheadPreview : which === 'signature' ? this.signaturePreview : this.stampPreview;
    const s = (v ?? '').toString().trim();
    if (!s) { sig.set(''); return; }
    if (/^(https?:\/\/|data:)/i.test(s)) { sig.set(s); return; }
    const cached = this.imgUrls()[s];
    if (cached) { sig.set(cached); return; }
    this.fileSvc.url(s).subscribe({
      next: (u) => {
        if (!u) return;
        this.imgUrls.update((m) => ({ ...m, [s]: u }));
        sig.set(u);
      },
    });
  }

  private blank() {
    return {
      id: 0, companyName: '', companyShortName: '', addressLine: '', customerCare: '', email: '',
      website: '', hospicashClaimNumber: '', docAssistanceNumber: '',
      letterheadImage: '', signatureImage: '', stampImage: '', isActive: true,
    };
  }

  openAdd() {
    this.editing.set(false);
    this.form.reset(this.blank());
    this.showModal.set(true);
  }

  openEdit(r: HospicashSetting) {
    this.editing.set(true);
    this.form.reset({
      id: r.id,
      companyName: r.companyName ?? '',
      companyShortName: r.companyShortName ?? '',
      addressLine: r.addressLine ?? '',
      customerCare: r.customerCare ?? '',
      email: r.email ?? '',
      website: r.website ?? '',
      hospicashClaimNumber: r.hospicashClaimNumber ?? '',
      docAssistanceNumber: r.docAssistanceNumber ?? '',
      letterheadImage: r.letterheadImage ?? '',
      signatureImage: r.signatureImage ?? '',
      stampImage: r.stampImage ?? '',
      isActive: r.isActive ?? true,
    });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    const v = this.form.getRawValue();
    if (this.form.get('companyName')?.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Company name is required.');
      return;
    }
    const trim = (x: any): string | null => ((x ?? '').toString().trim() || null);
    const dto: HospicashSettingDto = {
      id: this.editing() ? Number(v.id) : 0,
      companyName: (v.companyName || '').trim(),
      companyShortName: trim(v.companyShortName),
      addressLine: trim(v.addressLine),
      customerCare: trim(v.customerCare),
      email: trim(v.email),
      website: trim(v.website),
      hospicashClaimNumber: trim(v.hospicashClaimNumber),
      docAssistanceNumber: trim(v.docAssistanceNumber),
      letterheadImage: trim(v.letterheadImage),
      signatureImage: trim(v.signatureImage),
      stampImage: trim(v.stampImage),
      isActive: !!v.isActive,
    };
    const req = this.editing() ? this.svc.update(dto) : this.svc.insert(dto);
    this.saving.set(true);
    req.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save settings.');
          return;
        }
        this.toast.success(res?.message || (this.editing() ? 'Settings updated' : 'Settings created'));
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  toggleActive(r: HospicashSetting) {
    const next = !r.isActive;
    this.patchRow(r.id, { isActive: next });
    const req = next ? this.svc.enable(r.id) : this.svc.disable(r.id);
    req.subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<HospicashSetting>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  async remove(r: HospicashSetting) {
    const ok = await this.toast.confirm(
      'This company config will be permanently deleted. The welcome letter falls back to built-in defaults until another active config exists.',
      'Delete Hospicash setting?',
      'Delete',
    );
    if (!ok) return;
    this.svc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete.');
          return;
        }
        this.toast.success(res?.message || 'Setting deleted');
        this.load();
      },
    });
  }
}
